/* eslint-disable @typescript-eslint/no-explicit-any */

import { faMagnifyingGlass, faSliders, faX } from "@fortawesome/pro-regular-svg-icons";
import {
	ReactFlow,
	applyNodeChanges,
	applyEdgeChanges,
	Background,
	BackgroundVariant,
	Connection,
	Controls,
	Edge,
	Node,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";

import { useLocation, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import useLocalStorageState from "use-local-storage-state";

import {
	LINKEDIN_INMAIL_MESSAGE_MAX_LENGTH,
	LINKEDIN_INMAIL_SUBJECT_MAX_LENGTH,
	analyzeDisconnectedNodes,
	newStepDataFor,
	validateCampaignConfig,
} from "@signalist/shared-types";

import { ReactComponent as ChevronDoubleRight } from "@assets/icons/chevronDoubleRight.svg";

import { Icon, LoadingOverlay } from "@/components";

import { Button } from "@/components/Button/Button";

import {
	useGetListQuery,
	useUpdateListMutation,
	useLazyGetUnassignedAndDummyQuery,
	useGetAssignedToCampaignsQuery,
} from "@/pages/Private/redux/list/list.api";
import { listSelector, setCurrentLeadIndex } from "@/pages/Private/redux/list/list.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useGetSendDelayDefaultsQuery } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.api";
import { sendDelayDefaultsSelector } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.slice";
import { DEFAULT_NEW_STEP_DAYS } from "@/utils/stepDelayDefaults";
import { useSearch } from "@/utils";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { FilterModal, IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";
import {
	EmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
	LinkedinInmailNodeConfig,
} from "@/pages/Private/pages/List/components/CampaignOverview/components/nodes";
import { autoLayoutHorizontal } from "@/pages/Private/pages/List/components/CampaignOverview/utils/autoLayoutHorizontal";

import { edgeTypes } from "@/pages/Private/pages/List/components/CampaignOverview/config/edge-types";
import {
	CampaignNodeTypes,
	handleAddStepProps,
	nodeTypes,
} from "@/pages/Private/pages/List/components/CampaignOverview/config/node-types";

import { Lead } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/Lead";

import { classNames } from "@/utils/classNames";
import { SvgIcon } from "@/components/Icon/SvgIcon";

import { getSuggestions } from "@/pages/Private/pages/List/utils/column-utils";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { EmailEditor } from "@/pages/Private/pages/Account/components/EmailEditor";

import { LinkedinEditor } from "@/pages/Private/pages/Account/components/LinkedinEditor";

import {
	AiSnippets,
	AiSnippetsHandle,
} from "@/pages/Private/pages/Messages/components/AiSnippets/AiSnippets";
import { Variables } from "@/pages/Private/pages/Messages/components/Variables/Variables";
import { Spintax, SpintaxHandle } from "@/pages/Private/pages/Messages/components/Spintax/Spintax";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import {
	hydrateBuilderCampaign,
	highestCampaignNodeId,
} from "../utils/hydrateBuilderCampaign.util";

import { MyNode, NodeData, StarterNode } from "../types/node-data.types";
import {
	canvasReplacedHandler,
	selectedNodeMessageUpdates,
} from "../utils/campaignCanvasSelection";
import { useBuilderPreviewInbox } from "../utils/useBuilderPreviewInbox";
import { findChildrenNodes, findPreviousNode } from "../utils/campaignGraphTraversal.util";
import { hasPrecedingNodeOfType } from "../utils/hasPrecedingNodeOfType.util";
import { setSubjectOnNodeData } from "../utils/setSubjectOnNodeData";
import { CampaignEmailSubjectEditor } from "../components/CampaignEmailSubjectEditor";

import { SearchModal } from "../../SearchModal";
import { useGlobalNodesData } from "../components/NodesContextProvider";
import { useNewNodeConditionModal } from "../utils/useNewNodeConditionModal";
import { buildConditionalValue, ConditionalNodeKind } from "../utils/buildConditionalValue";
import { resolveNewStepDays } from "../utils/resolveNewStepDays";
import { decorateCampaignNodes } from "../utils/decorateCampaignNodes.util";
import { isInmailSenderMissing } from "../utils/inmailSender.util";
import { AddStepOptions } from "../types/step-insertion.types";
import { NodePanelHeader } from "../components/NodePanelHeader";
import { TestMessageBanner } from "../components/TestMessageBanner";
import { LinkedinInviteStepPanel } from "../components/InviteStep/LinkedinInviteStepPanel";
import { SendTestMessageModal } from "../components/SendTestMessageModal";
import { resolveEmailSubject } from "../utils/resolveEmailSubject";
import { resolveMessageSignature } from "../utils/resolveMessageSignature";
import { resolveStructureLock } from "../utils/resolveStructureLock";
import { CampaignPageHandle } from "../types/campaign-page.types";
import { useCampaignSocket } from "../../../providers/CampaignSocketContext";
import { CampaignConflictModal } from "../components/CampaignConflictModal";
import { StoredCampaignEntry } from "../utils/campaignRevision.util";
import { useAcknowledgedCampaign } from "../utils/use-acknowledged-campaign";
import { CampaignSaveStatus, useCampaignAutosave } from "../utils/useCampaignAutosave";

// Re-exported so the many `from ".../pages/CampaignPage"` importers keep working.
export type { CampaignPageHandle, MyNode, NodeData, StarterNode };

export const CampaignPage = forwardRef<
	CampaignPageHandle,
	{
		isOpened: boolean;
		handleClose: () => void;
		title: string;
		campaignUuid: string;
		/** Authoritative lock from the parent (PREC-3249) — same value as the displayed lock icon. */
		isLocked?: boolean;
		/** Opens the lock explainer when a locked campaign's add-step control is used (PREC-3786). */
		onLockedStructureEdit?: () => void;
		setCurrentlySelectedCampaignIsValid: (isValid: boolean) => void;
		setCurrentErrors: (errors: string[]) => void;
		/** Lifts the autosave state to the header chip, like validity and errors above. */
		setSaveStatus: (status: CampaignSaveStatus) => void;
		onCampaignSaved?: (entry: StoredCampaignEntry) => void;
	}
>(
	(
		{
			isOpened,
			title,
			campaignUuid,
			isLocked,
			onLockedStructureEdit,
			setCurrentlySelectedCampaignIsValid,
			setCurrentErrors,
			setSaveStatus,
			onCampaignSaved,
		},
		ref
	) => {
		const { id } = useParams();

		const location = useLocation();

		const [sawPreviewInfoModal, setSawPreviewInfoModal] = useLocalStorageState(
			"sawPreviewOnlyInfoModal",
			{
				defaultValue: false,
			}
		);

		const [sawCampaignInfoModal, setSawCampaignInfoModal] = useLocalStorageState(
			"sawCampaignInfoModal",
			{
				defaultValue: false,
			}
		);

		const { eventBasedState } = useGlobalNodesData();
		const nextHasNextNodeRef = useRef<boolean>(false);
		const nextHasNextYesNodeRef = useRef<boolean>(false);
		const nextHasNextNoNodeRef = useRef<boolean>(false);
		const {
			renderModal,
			renderConditionalChoiceModal,
			setShowConditionalChoice,
			setShowNewNodeModal,
			conditionalChoiceType,
		} = useNewNodeConditionModal();

		const isAiSnippetActive = useIsFeatureActive(FeaturesCodes.AI_SNIPPET);

		const { sortingInbox, inbox, currentLeadIndex } = useAppSelector(listSelector);

		const { t } = useTranslation();

		const [showLeads, setShowLeads] = useState<boolean>(false);

		const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);

		const { data: listData } = useGetListQuery(+(id || 0), {
			refetchOnMountOrArgChange: true,
		});

		const { data: assignedToCampaignsData } = useGetAssignedToCampaignsQuery(
			{ listId: +(id || 0) },
			{ skip: !id, refetchOnMountOrArgChange: true }
		);

		const { data: sendingUsers } = useGetSendersUsersQuery();

		// Structural lock (PREC-3853): locked once a step of the sequence was actually sent. Blocks
		// delete/condition/sender edits; text edits and appending a step at the end stay allowed.
		const isStructureLocked = useMemo(
			() =>
				resolveStructureLock({
					isLocked,
					campaignStatusInfo: listData?.campaignStatuses?.[campaignUuid],
					assignedCampaign: assignedToCampaignsData?.data?.find(
						(entry) => entry.campaignId === campaignUuid
					),
				}),
			[isLocked, listData, assignedToCampaignsData, campaignUuid]
		);

		// Mirror the lock into a ref so the stable ([]-dep) ReactFlow callbacks below read the
		// current value without being recreated (avoids stale closures).
		const isStructureLockedRef = useRef(isStructureLocked);

		isStructureLockedRef.current = isStructureLocked;

		// A locked campaign keeps its add-step control, but it explains the lock instead of adding
		// (PREC-3786). Passing `undefined` hides the control entirely, which reads as a broken button.
		const lockedAddStep = useMemo(
			() => (onLockedStructureEdit ? () => onLockedStructureEdit() : undefined),
			[onLockedStructureEdit]
		);

		const { listData: listDataFromSockets } = useCampaignSocket();

		const { listData: freshestListData, acknowledge } = useAcknowledgedCampaign(
			listDataFromSockets,
			listData,
			campaignUuid,
			onCampaignSaved
		);
		const campaignConfigRaw = freshestListData?.campaignConfig?.[campaignUuid];
		const inmailSenderMissing = isInmailSenderMissing(
			campaignConfigRaw?.senders,
			sendingUsers?.data
		);

		const [updateList] = useUpdateListMutation();

		// PREC-4299: the delays a newly added outreach step starts with.
		useGetSendDelayDefaultsQuery();
		const sendDelayDefaults = useAppSelector(sendDelayDefaultsSelector);

		const handleAddStep = useCallback(
			({
				position,
				initiatorId,
				edgeType,
				nodeType,
				hasNextNode,
				hasNextYesNode,
				hasNextNoNode,
			}: handleAddStepProps) => {
				setShowNewNodeModal(true);
				newNodePositionRef.current = position;
				newNodeInitiatorIdRef.current = initiatorId || null;

				nextEdgeTypeRef.current = edgeType || "normalEdge";
				nextNodeTypeRef.current = nodeType || "emailNode";
				nextHasNextNodeRef.current = hasNextNode || false;
				nextHasNextYesNodeRef.current = hasNextYesNode || false;
				nextHasNextNoNodeRef.current = hasNextNoNode || false;
			},
			[]
		);

		const handleSetDays = useCallback((nodeId: string, days: number | null) => {
			nodesRef.current = nodesRef.current.map((node) => {
				if (node.id === nodeId) {
					node.data.days = days;
				}

				return node;
			});

			setGraphVersion((v) => v + 1);
			updateNow();
		}, []);

		const handleSetWaitUntil = useCallback((nodeId: string, waitUntil: boolean) => {
			nodesRef.current = nodesRef.current.map((node) => {
				if (node.id === nodeId) {
					node.data.waitUntil = waitUntil;
				}

				return node;
			});

			setGraphVersion((v) => v + 1);
			updateNow();
		}, []);

		const handleSetConditionalValue = useCallback(
			(
				nodeId: string,
				value: { property: string; value: string; title: string; currentType?: string }
			) => {
				nodesRef.current = nodesRef.current.map((node) => {
					if (node.id === nodeId) {
						node.data.conditionalValue = value;
					}

					return node;
				});

				setGraphVersion((v) => v + 1);
				updateNow();
			},
			[]
		);

		const initialNodes = useMemo(
			() => [
				{
					id: "n1",
					position: { x: -275, y: 0 },
					data: {
						id: 1,
						label: "Start oder so",
						// Locked campaigns (PREC-3249) can't add nodes at all — the control explains why.
						handleAddStep: isStructureLocked
							? lockedAddStep
							: () =>
									handleAddStep({
										position: { x: 0, y: 0 },
										initiatorId: "n1",
										edgeType: "normalEdge",
										nodeType: "starterNode",
										hasNextNode: false,
									}),
						level: 0,
					},
					draggable: false,
					type: "starterNode",
				},
			],
			[handleAddStep, isStructureLocked, lockedAddStep]
		);

		useEffect(() => {
			if (!campaignConfigRaw?.initialNodes) {
				return;
			}

			highestNodeIdRef.current = highestCampaignNodeId(campaignConfigRaw.initialNodes);
		}, [campaignConfigRaw]);

		const hydrateCampaign = useCallback(
			(entry: StoredCampaignEntry | undefined) =>
				hydrateBuilderCampaign(entry, initialNodes as unknown as MyNode[], {
					deleteNode: isStructureLocked ? undefined : handleDeleteNode,
					handleAddStep: isStructureLocked ? lockedAddStep : handleAddStep,
					getPreviousNode,
					getChildrenNodes,
					handleSetDays,
					handleSetWaitUntil,
					handleSetConditionalValue: isStructureLocked ? undefined : handleSetConditionalValue,
				}),
			[
				initialNodes,
				handleAddStep,
				handleSetDays,
				handleSetConditionalValue,
				handleSetWaitUntil,
				isStructureLocked,
				lockedAddStep,
			]
		);
		const campaignConfig = useMemo(
			() => hydrateCampaign(campaignConfigRaw),
			[campaignConfigRaw, hydrateCampaign]
		);

		const initialEdges: Edge[] = useMemo(() => {
			return campaignConfig?.initialEdges ?? [];
		}, [campaignConfig]);

		const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
		const [showSearchModal, setShowSearchModal] = useState<boolean>(false);

		// Track graph version to trigger re-renders / validations after mutating nodesRef/edgesRef
		const [graphVersion, setGraphVersion] = useState<number>(0);

		const edgesRef = useRef<Edge[]>(initialEdges);
		const [selectedNode, setSelectedNode] = useState<
			| MyNode
			| EmailNodeConfig
			| LinkedinInviteNodeConfig
			| LinkedinNodeConfig
			| LinkedinInmailNodeConfig
			| null
		>(null);

		const [isSendTestOpen, setIsSendTestOpen] = useState<boolean>(false);
		const [sendTestInviteVariant, setSendTestInviteVariant] = useState<
			"standard" | "premium" | undefined
		>(undefined);
		const aiSnippetsRef = useRef<AiSnippetsHandle>(null);
		const spintaxRef = useRef<SpintaxHandle>(null);

		const newNodePositionRef = useRef<{ x: number; y: number } | null>(null);
		const newNodeInitiatorIdRef = useRef<string | null>(null);

		const highestNodeIdRef = useRef(1);
		const nextEdgeTypeRef = useRef<"yesEdge" | "noEdge" | "normalEdge">("normalEdge");
		const nextNodeTypeRef = useRef<CampaignNodeTypes>("emailNode");

		const nodesRef = useRef<MyNode[]>(campaignConfig?.initialNodes ?? initialNodes);

		const hasPrecedingEmailNode = useMemo(
			() => hasPrecedingNodeOfType(selectedNode?.id || "", getPreviousNode, "emailNode"),
			[selectedNode]
		);

		const isValidCampaignConfig = useMemo(() => {
			if (!campaignConfigRaw) {
				setCurrentlySelectedCampaignIsValid(false);
				setCurrentErrors([]);

				return false;
			}

			// Variables resolve against the previewed lead when there is one, else the list's columns.
			const validKeys = inbox[currentLeadIndex]
				? getSuggestions(inbox[currentLeadIndex], true, undefined, listData?.event)
				: getSuggestions(undefined, true, listData?.columns, listData?.event);

			// The rulebook itself lives in `validateCampaignConfig` (PREC-3952) so the campaign table
			// and the assignment pickers can say *why* a campaign cannot run — while it sat here, the
			// reasons existed only for the campaign currently open in this builder.
			const { isValid, errors } = validateCampaignConfig({
				campaign: campaignConfigRaw,
				nodes: nodesRef.current,
				edges: edgesRef.current,
				validKeys,
				senderEmails: sendingUsers?.data?.flatMap((user) => user.emails ?? []) ?? [],
			});

			setCurrentlySelectedCampaignIsValid(isValid);
			setCurrentErrors(errors.map((error) => error.message));

			return isValid;
		}, [graphVersion, campaignConfigRaw, inbox, currentLeadIndex, sendingUsers]);

		// Seeding and saving the canvas are bound to one campaign + revision (PREC-4026).
		const {
			updateNow,
			flush,
			flushAndPause,
			resumeAutoSave,
			resumeAutoSaveAfterSettings,
			saveState,
			lastSavedAt,
			retrySave,
			conflict,
			keepMine,
			takeTheirs,
		} = useCampaignAutosave({
			listId: +(id || 0),
			campaignUuid,
			title,
			isValid: isValidCampaignConfig,
			nodesRef,
			edgesRef,
			seed: campaignConfig,
			hydrateCampaign,
			fallbackNodes: initialNodes as unknown as MyNode[],
			storedCampaign: campaignConfigRaw,
			onAcknowledged: acknowledge,
			campaignConfigMap: freshestListData?.campaignConfig,
			updateList,
			onCanvasReplaced: canvasReplacedHandler(
				() => {
					highestNodeIdRef.current = highestCampaignNodeId(nodesRef.current);
					setGraphVersion((v) => v + 1);
				},
				() => setSelectedNode(null)
			),
		});

		// The chip lives in the header, so the state is lifted the same way validity and the error
		// list are. Until PREC-4151 the only signal was a transient "Saving..." snackbar, and a
		// rejected save had none at all.
		useEffect(() => {
			setSaveStatus({ state: saveState, lastSavedAt, retry: retrySave });
		}, [saveState, lastSavedAt, retrySave, setSaveStatus]);

		useEffect(() => {
			if (isValidCampaignConfig !== campaignConfigRaw?.isValid) {
				updateNow("validity");
			}
		}, [isValidCampaignConfig]);

		const dispatch = useAppDispatch();

		const { page, setPage, limit, query, setSearchValue, searchValue } = useSearch();

		const [getUnassigned, { isFetching }] = useLazyGetUnassignedAndDummyQuery();

		const getFilterFromUrl = () => {
			const params = new URLSearchParams(location.search);

			return JSON.parse(params.get("filter") || "{}");
		};

		const [filter, setFilter] = useState<IFilterWithValues | undefined>(() => getFilterFromUrl());

		useImperativeHandle(ref, () => ({
			flush,
			flushAndPause,
			resumeAutoSave,
			resumeAutoSaveAfterSettings,
			removeDisconnectedNodes: handleRemoveDisconnectedNodes,
		}));

		useBuilderPreviewInbox(
			{ page, limit, query, sorting: sortingInbox, listId: id ? +id : undefined, filter },
			getUnassigned
		);

		// Both stay `function` declarations: they are referenced above, inside the `campaignConfig`
		// memo, and only hoisting makes that legal. As `const` arrows they are in the TDZ there.
		function getPreviousNode(nodeId: string): Node<NodeData> | null {
			return findPreviousNode(nodesRef.current, edgesRef.current, nodeId);
		}

		function getChildrenNodes(nodeId: string): Node<NodeData>[] | null {
			return findChildrenNodes(nodesRef.current, edgesRef.current, nodeId);
		}

		function handleDeleteNode(nodeId: string) {
			const connectingEdges = edgesRef.current.filter(
				(edge) => edge.source === nodeId || edge.target === nodeId
			);

			const removeEdgeIds = connectingEdges.map((edge) => edge.id);

			let incoming: Edge | null = null;
			let outgoing: Edge | null = null;

			for (const edge of connectingEdges) {
				if (edge.target === nodeId) {
					incoming = edge;
				}

				if (edge.source === nodeId) {
					outgoing = edge;
				}
			}

			let newEdge: Edge | null = null;

			if (incoming && outgoing) {
				newEdge = {
					id: `e${incoming.source}-${outgoing.target}`,
					source: incoming.source,
					target: outgoing.target,
					type: incoming?.type ?? outgoing?.type ?? "normalEdge",
					sourceHandle: incoming?.sourceHandle ?? outgoing?.sourceHandle ?? "normalEdge",
				};
			}

			const newNodes = nodesRef.current.filter((node) => node.id !== nodeId);

			nodesRef.current = newNodes;

			const remainingEdges = edgesRef.current.filter((edge) => !removeEdgeIds.includes(edge.id));

			edgesRef.current = newEdge ? [...remainingEdges, newEdge] : remainingEdges;

			nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

			// Force React to re-render so ReactFlow receives updated nodes/edges
			setGraphVersion((v) => v + 1);
			updateNow();
		}

		const onNodesChange = useCallback((changes: any) => {
			// Locked campaigns (PREC-3249) can't remove steps — drop deletion changes (e.g. via the
			// keyboard) so the structural lock can't be bypassed at the canvas level.
			const applied = isStructureLockedRef.current
				? changes.filter((change: any) => change.type !== "remove")
				: changes;

			nodesRef.current = applyNodeChanges(applied, nodesRef.current);
			// Only trigger update for meaningful changes (not selection, dimensions, or initial positioning)
			const hasRelevantChange = applied.some(
				(change: any) =>
					change.type !== "select" && change.type !== "dimensions" && change.type !== "position"
			);

			if (hasRelevantChange) {
				updateNow();
			}
		}, []);
		const onEdgesChange = useCallback((changes: any) => {
			// Locked campaigns can't rewire/remove edges — drop edge removals (keyboard delete etc.).
			const applied = isStructureLockedRef.current
				? changes.filter((change: any) => change.type !== "remove")
				: changes;

			edgesRef.current = applyEdgeChanges(applied, edgesRef.current);

			// Only trigger update for meaningful changes (not selection)
			const hasRelevantChange = applied.some((change: any) => change.type !== "select");

			if (hasRelevantChange) {
				updateNow();
			}
		}, []);

		const onConnect = useCallback((connection: Connection) => {
			// Locked campaigns can't create new connections by dragging (structural change).
			if (isStructureLockedRef.current) {
				return;
			}

			// Only accept connections that reconnect an orphaned sub-chain to the
			// reachable graph: the source must be an open outgoing slot on a
			// reachable node, and the target must be an orphan root.
			if (connection.source === connection.target) {
				return;
			}

			const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);

			if (analysis.orphanedNodeIds.length === 0) {
				return;
			}

			const handle =
				(connection.sourceHandle as "normalEdge" | "yesEdge" | "noEdge" | null) ?? "normalEdge";
			const isOpenSource = analysis.openSources.some(
				(slot) => slot.nodeId === connection.source && slot.handle === handle
			);
			const isOrphanRoot = analysis.orphanRootIds.includes(connection.target ?? "");

			if (!isOpenSource || !isOrphanRoot) {
				return;
			}

			const newEdge: Edge = {
				id: `e${connection.source}-${connection.target}`,
				source: connection.source as string,
				target: connection.target as string,
				type: handle,
				sourceHandle: handle,
			};

			edgesRef.current = [...edgesRef.current, newEdge];
			nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

			setGraphVersion((v) => v + 1);
			updateNow();
		}, []);

		const handleRemoveDisconnectedNodes = useCallback(() => {
			const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);

			if (analysis.orphanedNodeIds.length === 0) {
				return;
			}

			const orphanedSet = new Set(analysis.orphanedNodeIds);

			nodesRef.current = nodesRef.current.filter((node) => !orphanedSet.has(node.id));
			edgesRef.current = edgesRef.current.filter(
				(edge) => !orphanedSet.has(edge.source) && !orphanedSet.has(edge.target)
			);

			nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

			setGraphVersion((v) => v + 1);
			updateNow();
		}, []);

		const closeNewNodeModal = useCallback(() => {
			setShowNewNodeModal(false);
		}, []);

		function handleSetCurrentLead(index: number) {
			dispatch(setCurrentLeadIndex(index));
		}

		function handleAddConditionalNode(
			edgeType?: "yesEdge" | "noEdge" | "normalEdge",
			type?: ConditionalNodeKind
		) {
			setShowConditionalChoice(false);
			const newInitiatorNumber = highestNodeIdRef.current + 1;
			const newId = "n" + newInitiatorNumber;

			const position = newNodePositionRef.current;
			const initiatorId = newNodeInitiatorIdRef.current;

			highestNodeIdRef.current = newInitiatorNumber;

			let level = 0;

			if (!position) {
				return;
			}

			if (!initiatorId) {
				return;
			}

			let connectingNodeOfInitiator: Edge | null = null;

			if (initiatorId) {
				const initiatorNode = nodesRef.current.find((node) => node.id === initiatorId);

				level = (initiatorNode?.data.level || 0) + 1;

				connectingNodeOfInitiator =
					edgesRef.current.find(
						(edge) => edge.source === initiatorId && edge.type === nextEdgeTypeRef.current
					) || null;
			}

			const conditionalValue = buildConditionalValue(type);

			const newNode: Node<NodeData> = {
				id: newId,
				type: "conditionalNode",
				position: { x: 0, y: position.y + 200 },
				data: {
					id: newInitiatorNumber,
					label: `Node ${newId}`,
					handleAddStep,
					source: initiatorId,
					deleteNode: isStructureLocked ? undefined : handleDeleteNode,
					level: level,
					getPreviousNode: getPreviousNode,
					getChildrenNodes: getChildrenNodes,
					edgeType: nextEdgeTypeRef.current as unknown as typeof edgeTypes,
					nodeType: nextNodeTypeRef.current as unknown as typeof nodeTypes,
					// Persist the default 1-day window like every other added node; without it the
					// backend adds no delay and checks acceptance the same day (see handleAddEmailNode).
					days: DEFAULT_NEW_STEP_DAYS,
					handleSetDays: handleSetDays,
					handleSetWaitUntil: handleSetWaitUntil,
					handleSetConditionalValue: isStructureLocked ? undefined : handleSetConditionalValue,
					conditionalValue,
				},
				draggable: false,
			};

			nodesRef.current = [...nodesRef.current, newNode as MyNode];

			if (initiatorId) {
				const newEdge: Edge = {
					id: `e${initiatorId}-${newId}`,
					source: initiatorId,
					target: newId,
					type: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
					sourceHandle: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
				};

				let newEdges = [...edgesRef.current, newEdge];

				if (connectingNodeOfInitiator) {
					const secondEdge: Edge = {
						id: `e${newId}-${connectingNodeOfInitiator.target}`,
						source: newId,
						target: connectingNodeOfInitiator.target,
						sourceHandle: edgeType || "yesEdge",
						type: edgeType || "yesEdge",
					};

					newEdges = [
						...newEdges.filter((edge) => edge.id !== connectingNodeOfInitiator?.id),
						secondEdge,
					];
				}

				edgesRef.current = [...newEdges];
			}

			nextEdgeTypeRef.current = "normalEdge";

			nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

			// Force React to re-render so ReactFlow receives updated nodes/edges
			setGraphVersion((v) => v + 1);

			closeNewNodeModal();
		}

		const handleChangeNodeType = useCallback((nodeId: string, nodeType: CampaignNodeTypes) => {
			const node = nodesRef.current.find((node) => node.id === nodeId) as Node<NodeData>;

			if (!node) {
				return;
			}

			node.type = nodeType;
			// A step that becomes an invite is a new invite step (PREC-4426); an invite keeps its switch.
			node.data = { ...newStepDataFor(nodeType), ...node.data };

			nodesRef.current = [...nodesRef.current];

			setGraphVersion((v) => v + 1);
			setSelectedNode(null);
			setSelectedNode(node as unknown as MyNode);
		}, []);

		function handleAddEmailNode(type: CampaignNodeTypes, options?: AddStepOptions) {
			const newInitiatorNumber = highestNodeIdRef.current + 1;
			const newId = "n" + newInitiatorNumber;

			const position = newNodePositionRef.current;

			newNodePositionRef.current = null;

			const initiatorId = newNodeInitiatorIdRef.current;

			newNodeInitiatorIdRef.current = null;

			highestNodeIdRef.current = newInitiatorNumber;

			let level = 0;

			if (!position) {
				return;
			}

			if (!initiatorId) {
				return;
			}

			let connectingNodeOfInitiator: Edge | null = null;

			if (initiatorId) {
				const initiatorNode = nodesRef.current.find((node) => node.id === initiatorId);

				level = (initiatorNode?.data.level || 0) + 1;

				connectingNodeOfInitiator =
					edgesRef.current.find(
						(edge) => edge.source === initiatorId && edge.type === nextEdgeTypeRef.current
					) || null;
			}

			const newNode: Node<NodeData> = {
				id: newId,
				type: type,
				position: { x: 0, y: position.y + 200 },
				data: {
					id: newInitiatorNumber,
					label: `Node ${newId}`,
					handleAddStep,
					source: initiatorId,
					deleteNode: isStructureLocked ? undefined : handleDeleteNode,
					level: level,
					getPreviousNode: getPreviousNode,
					getChildrenNodes: getChildrenNodes,
					edgeType: nextEdgeTypeRef.current as unknown as typeof edgeTypes,
					nodeType: nextNodeTypeRef.current as unknown as typeof nodeTypes,
					days: resolveNewStepDays({
						nodeType: type,
						parentId: initiatorId,
						nodes: nodesRef.current,
						edges: edgesRef.current,
						sendDelayDefaults,
					}),
					handleSetDays: handleSetDays,
					...newStepDataFor(type),
					handleSetWaitUntil: handleSetWaitUntil,
				},
				draggable: false,
			};

			nodesRef.current = [...nodesRef.current, newNode as MyNode];

			if (initiatorId) {
				const newEdge: Edge = {
					id: `e${initiatorId}-${newId}`,
					source: initiatorId,
					target: newId,
					type: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
					sourceHandle: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
				};

				let newEdges = [...edgesRef.current, newEdge];

				if (connectingNodeOfInitiator) {
					const secondEdge: Edge = {
						id: `e${newId}-${connectingNodeOfInitiator.target}`,
						source: newId,
						target: connectingNodeOfInitiator.target,
						sourceHandle: "normalEdge",
						type: "normalEdge",
					};

					newEdges = [
						...newEdges.filter((edge) => edge.id !== connectingNodeOfInitiator?.id),
						secondEdge,
					];
				}

				edgesRef.current = [...newEdges];
			}

			nextEdgeTypeRef.current = "normalEdge";

			nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

			// Force React to re-render so ReactFlow receives updated nodes/edges
			setGraphVersion((v) => v + 1);

			closeNewNodeModal();

			setSelectedNode(newNode as MyNode);

			// PREC-3897: "Lead accepted invitation" right below the new invitation; whatever followed
			// the invitation moves onto its Yes path.
			if (type === "linkedinInviteNode" && options?.withAcceptedCheck) {
				newNodeInitiatorIdRef.current = newId;
				newNodePositionRef.current = newNode.position;
				nextNodeTypeRef.current = type;
				handleAddConditionalNode("yesEdge", "linkedinInviteNode");
			}
		}

		useEffect(() => {
			const newNodes = decorateCampaignNodes(nodesRef.current, edgesRef.current, {
				inmailSenderMissing,
			});

			if (JSON.stringify(newNodes) !== JSON.stringify(nodesRef.current)) {
				nodesRef.current = newNodes;
			}
		}, [nodesRef.current, edgesRef.current, inmailSenderMissing]);

		const onNodeClick = useCallback(
			(event: React.MouseEvent, node: Node) => {
				if (isFetching) {
					return;
				}

				if (
					node.type !== "starterNode" &&
					node.type !== "conditionalNode" &&
					node.type !== "linkedinVisitNode"
				) {
					setSelectedNode(node as MyNode);
				} else {
					setSelectedNode(null);
				}
			},
			[isFetching]
		);

		// Persists message updates (AI snippets / variables panel inserts) into the
		// selected node, the node graph and the autosave queue.
		const persistSelectedNodeMessages = (results: string[]) => {
			if (!selectedNode) {
				return;
			}

			const updates = selectedNodeMessageUpdates(selectedNode.type, !!eventBasedState, results);

			setSelectedNode({
				...selectedNode,
				data: {
					...selectedNode?.data,
					...updates,
				},
			} as EmailNodeConfig);

			nodesRef.current = nodesRef.current.map((node) => {
				if (node.id === selectedNode?.id) {
					return { ...node, data: { ...node.data, ...updates } };
				}

				return node;
			});

			setGraphVersion((v) => v + 1);
			updateNow();
		};

		return isOpened ? (
			<div className="flex flex-col justify-between items-start w-full h-[100%] bg-white">
				<div className="flex w-full h-full flex-row relative bg-brand-white overflow-x-hidden">
					<div
						className={`bg-white w-[467px] min-w-[467px] transition-all z-15 duration-300 h-full border-r pl-0 border-t border-border-light rounded-tr-xl  pt-2 pb-0 mb-4 relative ${
							showLeads ? "translate-x-0" : "translate-x-[0] !w-[35px] !min-w-[35px] pl-4"
						}`}
					>
						<div className="absolute top-0 right-0 z-[50000]">
							<div
								className={classNames(
									"flex justify-between items-center h-[48px] px-2 mb-4 w-full"
								)}
								onClick={(e: React.MouseEvent) => {
									e.stopPropagation();

									setShowLeads(!showLeads);
								}}
							>
								<SvgIcon
									className={classNames("w-[24px] h-[24px]", showLeads ? "rotate-180" : "rotate-0")}
									svgIcon={ChevronDoubleRight}
								/>
							</div>
						</div>
						<div
							className={classNames(
								"overflow-x-hidden h-full scrollbar-hide",
								showLeads ? "flex flex-col" : "hidden"
							)}
						>
							<div className="flex flex-col gap-2 pb-2 border-b border-divider">
								<div className="flex flex-row gap-2 flex-wrap px-4">
									<div className="w-[fit-content] mr-2">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											image={<Icon className="mr-2" icon={faSliders} />}
											size={ButtonSize.M}
											title={`${ts("filter")}${
												filter && Object.keys(filter).length > 0
													? ` (${Object.keys(filter).length})`
													: ""
											}`}
											onClick={() => setShowFilterModal(true)}
										/>
									</div>
									<div className="w-[fit-content] mr-2">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											image={<Icon className="mr-2" icon={faMagnifyingGlass} />}
											size={ButtonSize.M}
											title={`${t("basics.toSearch")}`}
											onClick={() => setShowSearchModal(true)}
										/>
									</div>
								</div>

								{!sawPreviewInfoModal && (
									<div className="px-2 relative">
										<Icon
											className=" z-10 w-3 h-3 font-semibold text-text-meta cursor-pointer absolute top-2 right-6 bg-brand-white rounded-full p-1 border border-border-light hover:bg-blue-action-200 hover:text-text"
											icon={faX}
											onClick={() => setSawPreviewInfoModal(true)}
										/>
										<div className="flex flex-row justify-between w-full items-start bg-background-warning p-4 rounded-xl gap-6 ">
											<div className="w-full relative">
												<div className="flex flex-col gap-2">
													<div className="text-sm text-text font-bold">Preview only</div>
													<div className="text-sm text-text">
														You can not directly send emails or LinkedIn invites from this page. The
														selection of the leads is only for preview purposes.
													</div>
												</div>
											</div>
										</div>
									</div>
								)}
								<div className="flex flex-col pt-0 max-h-[100%] w-full scrollbar-hide">
									{isFetching ? <LoadingOverlay /> : <></>}
									{inbox.map((row: ListPerson) => (
										<Lead
											key={`${row.id}-${row.prospect?.id}`}
											pKey={`${row.id}-${row.prospect?.id}`}
											handleSetCurrentLead={handleSetCurrentLead}
											listId={+(id || 0)}
											row={row}
										/>
									))}
								</div>
							</div>
						</div>
					</div>
					<div
						className={classNames(
							"bg-brand-white h-full w-[calc(100%-500px)] relative transition-all duration-300",
							showLeads ? "w-full left-auto" : "w-full left-0",
							isFetching && "pointer-events-none blur-sm opacity-50"
						)}
					>
						<ReactFlow
							defaultViewport={{ x: 500, y: 100, zoom: 1 }}
							// Locked campaigns can't delete steps/edges via the keyboard (PREC-3249).
							deleteKeyCode={isStructureLocked ? null : undefined}
							edgeTypes={edgeTypes}
							edges={edgesRef.current}
							nodeTypes={nodeTypes}
							nodes={nodesRef.current}
							snapGrid={[10, 10]}
							snapToGrid={true}
							onClick={() => {
								setSelectedNode(null);
							}}
							onConnect={onConnect}
							onEdgesChange={onEdgesChange}
							onNodeClick={(e, node) => {
								e.stopPropagation();
								e.preventDefault();

								setSelectedNode(null);
								setTimeout(() => {
									onNodeClick(e, node);
								}, 1);
							}}
							onNodesChange={onNodesChange}
						>
							<Background variant={BackgroundVariant.Dots} />
							<Controls />
						</ReactFlow>

						{!sawCampaignInfoModal && (
							<div className="absolute bottom-0 left-0 w-full h-fit flex items-start justify-start z-[450] bg-white p-2 w-full border-t border-border-light">
								<div className="w-full  relative">
									<Icon
										className="w-4 h-4 text-text-disabled cursor-pointer absolute top-0 right-0 bg-brand-white rounded-full p-1 border border-border-light"
										icon={faX}
										onClick={() => setSawCampaignInfoModal(true)}
									/>
									<div className="flex flex-row flex-wrap gap-1 text-sm text-text-meta">
										{isAiSnippetActive ? (
											<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
												Configure your multi-channel sequence for all leads using variables and AI
												snippets. You can test messages by selecting any lead from the left lead
												lead panel.
											</span>
										) : (
											<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
												Configure your multi-channel sequence for all leads using variables. You can
												test messages by selecting any lead from the left lead lead panel.
											</span>
										)}
										<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
											Sequences stop automatically as soon as the lead responds to a mesage.
										</span>
										<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
											If a step fails, the next step will be executed
										</span>
									</div>
								</div>
							</div>
						)}
					</div>
					{selectedNode && (
						<div className="absolute top-0 right-0 w-1/3 min-w-[400px] h-full bg-white border-l border-t border-gray-200 p-4 scrollbar-hide overflow-y-auto">
							<NodePanelHeader
								level={selectedNode.data.level}
								nodeType={selectedNode.type as CampaignNodeTypes}
								// Locked campaigns can't change sender selection or step channel (PREC-3249).
								disabled={isStructureLocked || Boolean(selectedNode.data?.alreadySent)}
								selectedSenders={
									(selectedNode.data?.senders as DropdownWithCheckboxesItem[]) || undefined
								}
								senders={campaignConfigRaw?.senders}
								onChangeNodeType={(nodeType) => {
									handleChangeNodeType(selectedNode.id, nodeType);
								}}
								onChangeSenders={(senders) => {
									setSelectedNode({
										...selectedNode,
										data: {
											...selectedNode?.data,
											senders,
										},
									} as unknown as MyNode);

									nodesRef.current = nodesRef.current.map((node) => {
										if (node.id === selectedNode?.id) {
											return { ...node, data: { ...node.data, senders } };
										}

										return node;
									});

									updateNow();
								}}
							/>

							{["emailNode", "linkedinInmailNode"].includes(selectedNode.type) && (
								<div className="w-full">
									<CampaignEmailSubjectEditor
										nodeId={selectedNode.id}
										nodeType={selectedNode.type}
										subject={
											eventBasedState
												? (selectedNode?.data.alternateSubject ?? "")
												: (selectedNode?.data.subject ?? "")
										}
										hasPrecedingEmailNode={hasPrecedingEmailNode}
										eventBasedState={eventBasedState}
										getPreviousNode={getPreviousNode}
										currentLeadIndex={currentLeadIndex}
										listColumns={listData?.columns}
										listEvent={listData?.event}
										maxLength={
											selectedNode.type === "linkedinInmailNode"
												? LINKEDIN_INMAIL_SUBJECT_MAX_LENGTH
												: undefined
										}
										onChange={(value: string) => {
											setSelectedNode({
												...selectedNode,
												data: setSubjectOnNodeData(selectedNode.data, value, eventBasedState),
											} as unknown as MyNode);

											nodesRef.current = nodesRef.current.map((node) =>
												node.id === selectedNode?.id
													? {
															...node,
															data: setSubjectOnNodeData(node.data, value, eventBasedState),
														}
													: node
											);

											setGraphVersion((v) => v + 1);
											updateNow();
										}}
									/>
								</div>
							)}

							{selectedNode.type === "emailNode" && (
								<div className="w-full mt-4">
									<EmailEditor
										listColumns={listData?.columns}
										listEvent={listData?.event}
										listId={listData?.id}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
										currentLeadIndex={currentLeadIndex}
										handleChange={(value: string) => {
											if (eventBasedState) {
												setSelectedNode({
													...selectedNode,
													data: {
														...selectedNode?.data,
														alternateMessage: value,
													},
												} as EmailNodeConfig);
											} else {
												setSelectedNode({
													...selectedNode,
													data: {
														...selectedNode?.data,
														message: value,
													},
												} as EmailNodeConfig);
											}

											nodesRef.current = nodesRef.current.map((node) => {
												if (node.id === selectedNode?.id) {
													if (eventBasedState) {
														return { ...node, data: { ...node.data, alternateMessage: value } };
													} else {
														return { ...node, data: { ...node.data, message: value } };
													}
												}

												return node;
											});

											setGraphVersion((v) => v + 1);
											updateNow();
										}}
										placeholder="Write your message here"
										sender={campaignConfigRaw?.senders?.[0]}
										signature={resolveMessageSignature(selectedNode, eventBasedState)}
									/>
								</div>
							)}
							{["linkedinInviteNode"].includes(selectedNode.type) && (
								<div className="w-full mt-4">
									<LinkedinInviteStepPanel
										currentLeadIndex={currentLeadIndex}
										eventBasedState={eventBasedState}
										listColumns={listData?.columns}
										listEvent={listData?.event}
										listId={listData?.id}
										nodesRef={nodesRef}
										selectedNode={selectedNode}
										sender={campaignConfigRaw?.senders?.[0]}
										setSelectedNode={setSelectedNode}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
										onUpdate={updateNow}
									/>
								</div>
							)}
							{["linkedinNode", "linkedinInmailNode"].includes(selectedNode.type) && (
								<div className="w-full mt-4">
									<LinkedinEditor
										listColumns={listData?.columns}
										listEvent={listData?.event}
										listId={listData?.id}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
										currentLeadIndex={currentLeadIndex}
										handleChange={(value: string) => {
											if (eventBasedState) {
												setSelectedNode({
													...selectedNode,
													data: {
														...selectedNode?.data,
														alternateMessage: value,
													},
												} as EmailNodeConfig);
											} else {
												setSelectedNode({
													...selectedNode,
													data: {
														...selectedNode?.data,
														message: value,
													},
												} as EmailNodeConfig);
											}

											nodesRef.current = nodesRef.current.map((node) => {
												if (node.id === selectedNode?.id) {
													if (eventBasedState) {
														return { ...node, data: { ...node.data, alternateMessage: value } };
													} else {
														return { ...node, data: { ...node.data, message: value } };
													}
												}

												return node;
											});

											setGraphVersion((v) => v + 1);
											updateNow();
										}}
										maxLength={
											selectedNode.type === "linkedinInmailNode"
												? LINKEDIN_INMAIL_MESSAGE_MAX_LENGTH
												: undefined
										}
										placeholder="Write your message here"
										sender={campaignConfigRaw?.senders?.[0]}
										signature={resolveMessageSignature(selectedNode, eventBasedState)}
									/>
								</div>
							)}
							{["emailNode", "linkedinNode", "linkedinInmailNode", "linkedinInviteNode"].includes(
								selectedNode.type
							) && (
								<div className="w-full pb-28">
									<TestMessageBanner
										listData={listData}
										message={selectedNode.data.message}
										nodeType={selectedNode.type as CampaignNodeTypes}
										premiumMessage={selectedNode.data.premiumMessage}
										onCreateAiSnippets={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onSendTest={(variant) => {
											setSendTestInviteVariant(variant);
											setIsSendTestOpen(true);
										}}
									/>
									{isAiSnippetActive && (
										<AiSnippets
											ref={aiSnippetsRef}
											listData={listData}
											selectedNode={selectedNode}
											withPersistance={persistSelectedNodeMessages}
										/>
									)}
									<Variables
										listData={listData}
										selectedNode={selectedNode}
										withPersistance={persistSelectedNodeMessages}
									/>
									<Spintax
										ref={spintaxRef}
										listData={listData}
										selectedNode={selectedNode}
										withPersistance={persistSelectedNodeMessages}
									/>
								</div>
							)}
						</div>
					)}
				</div>
				{selectedNode && isSendTestOpen && (
					<SendTestMessageModal
						campaignSenders={campaignConfigRaw?.senders}
						channel={selectedNode.type === "emailNode" ? "email" : "linkedin"}
						inviteVariant={
							selectedNode.type === "linkedinInviteNode" ? sendTestInviteVariant : undefined
						}
						isOpen={isSendTestOpen}
						listPersonId={inbox[currentLeadIndex]?.id}
						message={selectedNode.data.message ?? ""}
						nodeType={selectedNode.type}
						premiumMessage={
							selectedNode.type === "linkedinInviteNode"
								? (selectedNode.data.premiumMessage ?? "")
								: undefined
						}
						sendingUsers={sendingUsers?.data}
						subject={
							selectedNode.type === "emailNode"
								? resolveEmailSubject(selectedNode.id, getPreviousNode, selectedNode.data.subject)
								: undefined
						}
						onClose={() => {
							setIsSendTestOpen(false);
							setSendTestInviteVariant(undefined);
						}}
					/>
				)}
				{renderModal(
					handleAddEmailNode,
					handleAddConditionalNode,
					nextHasNextNodeRef.current,
					nextNodeTypeRef.current,
					nextEdgeTypeRef.current,
					nextHasNextYesNodeRef.current,
					nextHasNextNoNodeRef.current,
					{
						nodes: nodesRef.current,
						edges: edgesRef.current,
						initiatorId: newNodeInitiatorIdRef.current,
						inmailSenderMissing,
					}
				)}
				{renderConditionalChoiceModal(
					handleAddConditionalNode,
					conditionalChoiceType ? conditionalChoiceType : undefined
				)}

				{showFilterModal && listData && id && (
					<FilterModal
						filter={filter}
						listId={+id}
						listType={listData.sourceType}
						setFilter={(e) => {
							setPage(1);
							setFilter(e);
						}}
						setShowFilterModal={setShowFilterModal}
						showFilterModal={showFilterModal}
					/>
				)}
				{showSearchModal && (
					<SearchModal
						searchValue={searchValue}
						setSearchValue={setSearchValue}
						setShowFilterModal={setShowSearchModal}
						showFilterModal={showSearchModal}
					/>
				)}
				<CampaignConflictModal
					conflict={conflict}
					onKeepMine={() => void keepMine()}
					onTakeTheirs={takeTheirs}
				/>
			</div>
		) : (
			<></>
		);
	}
);
